import { useState, useMemo, useEffect } from "react";
import { Container } from "@/components/atoms";
import {
  FaqAccordionItem,
  FilterPillRow,
  type FilterOption,
} from "@/components/molecules";
import { Input } from "@/components/atoms";
import { siteFaqs } from "@/data";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "@/services/firebase";
import type { SiteFAQ } from "@/types";
import { Seo } from "@/components/seo";

export default function Faq() {
  const [faqs, setFaqs] = useState<SiteFAQ[]>(siteFaqs);
  const [category, setCategory] = useState("all");
  const [term, setTerm] = useState("");

  useEffect(() => {
    const q = query(collection(db, "faqs"), orderBy("order"));
    return onSnapshot(q, (snap) => {
      if (!snap.empty) {
        setFaqs(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SiteFAQ));
      }
    });
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(faqs.map((f) => f.category))),
    [faqs],
  );
  const options: FilterOption[] = [
    { value: "all", label: "All" },
    ...categories.map((c) => ({ value: c, label: c })),
  ];
  /*
   * DRB-67 — this page rendered the PRODUCT SearchBar, whose placeholder reads "Search products…"
   * and which navigates to /search on submit. A visitor typing a question on the FAQ page was
   * thrown off it into product search, and nothing on the page filtered the FAQs at all.
   */
  const t = term.trim().toLowerCase();
  const visible = faqs
    .filter((f) => category === "all" || f.category === category)
    .filter((f) => !t || `${f.question} ${f.answer}`.toLowerCase().includes(t))
    .sort((a, b) => a.order - b.order);

  return (
    <>
      <Seo
        title="FAQ"
        path="/faq"
        description="Frequently asked questions about DrBurns' ReLeaf products, shipping, subscriptions, and ordering."
      />

      <Container className="max-w-3xl py-12">
        <h1 className="font-condensed text-4xl font-bold text-night-blue">
          Frequently Asked Questions
        </h1>
        <div className="mt-6">
          <Input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search these questions…"
            aria-label="Search frequently asked questions"
          />
        </div>
        <div className="mt-4">
          <FilterPillRow
            options={options}
            active={category}
            onChange={setCategory}
          />
        </div>
        <div className="mt-6">
          {visible.length === 0 && (
            <p className="py-8 text-center text-sm text-night-blue/70">
              No questions match “{term.trim()}”. Try a different word, or email
              orders@drburnsreleaf.com and we will answer it.
            </p>
          )}
          {visible.map((f) => (
            <FaqAccordionItem
              key={f.id}
              question={f.question}
              answer={f.answer}
            />
          ))}
        </div>
      </Container>
    </>
  );
}
